import React, { useMemo } from 'react'
import { Box, Container, Divider, Typography } from '@mui/material'
import DynamicLenderDetailsBanner from './DynamicLenderDetailsBanner'
import { LenderDetails } from '../../../ApplicationFormContext'
import NextSteps from './NextSteps'
import { fireReactGAEvent } from '../../../../Questions/utils'
import { ArrowRight } from '@zuto/icons'
import Button from '@zuto/atoms/src/Mui/Button/Button'
import ContactOptions from './ContactOptions'
import WhyCustomersLoveZuto from './WhyCustomersLoveZuto'
import MyZutoAccountSection from './MyZutoAccountSection'
import LandingFooter from './LandingFooter'
import PartnerBrandHeader from './PartnerBrandHeader'
import ZutoTerms from './ZutoTerms'
import EventsService, { AppFormEventParams } from '../../../../../services/events-service'

interface FormlessLandingPageProps {
  lenderDetails?: LenderDetails
  experimentName: string
  source: string
  redirectUrl?: string
  eventsServiceUrl: string
  appFormEventParams?: AppFormEventParams
}

const FormlessLandingPage: React.FC<FormlessLandingPageProps> = ({ lenderDetails, experimentName, source, redirectUrl, eventsServiceUrl, appFormEventParams }) => {
  const eventsService = useMemo(() => (appFormEventParams ? EventsService(eventsServiceUrl, appFormEventParams) : undefined), [eventsServiceUrl, appFormEventParams])
  const isPreapproved = lenderDetails?.preapproved ?? false

  const handleClick = (buttonId: number) => {
    fireReactGAEvent({
      category: 'Application Form',
      action: 'FormlessContinueCtaClicked',
      label: experimentName,
      source
    })

    if (eventsService) {
      eventsService.sendEvent('ContinueToMyZutoCta', { buttonId })
    }

    if (redirectUrl) {
      window.location.replace(redirectUrl)
    }
  }

  return (
    <>
      <PartnerBrandHeader source={source} />
      <Box
        sx={{
          maxWidth: '100%',
          width: '100%',
          margin: '0 auto',
          textAlign: 'center'
        }}
      >
        <DynamicLenderDetailsBanner lenderDetails={lenderDetails} source={source} />
        <NextSteps isPreapproved={isPreapproved} source={source}/>

        <Container
          sx={{ marginTop: 8, paddingLeft: 5, paddingRight: 5 }}
          maxWidth="md"
        >
          <Button
            endSvgIcon={ArrowRight}
            data-testid="continue-to-account-button"
            onClick={() => handleClick(1)}
          >
            Go to My Zuto
          </Button>
          <ZutoTerms />
        </Container>

        <Divider
          sx={{
            my: 8,
            mx: 'auto',
            maxWidth: 600,
            px: 5,
            '&::before, &::after': {
              borderColor: 'divider'
            }
          }}
        >
          <Typography
            variant="bodySmall"
            sx={{
              mx: 4,
              textTransform: 'uppercase',
              color: 'text.secondary'
            }}
          >
            OR
          </Typography>
        </Divider>

        <ContactOptions
          eventsServiceUrl={eventsServiceUrl}
          appFormEventParams={appFormEventParams}
        />

        <WhyCustomersLoveZuto source={source} />
        <MyZutoAccountSection />

        <Container
          sx={{ marginTop: '32px', paddingLeft: 5, paddingRight: 5 }}
          maxWidth="md"
        >
          <Button
            endSvgIcon={ArrowRight}
            data-testid="go-to-my-zuto-button"
            onClick={() => handleClick(2)}
            fullWidth
          >
            Take me to My Zuto
          </Button>
          <ZutoTerms />
        </Container>

        <LandingFooter source={source} />
      </Box>
    </>
  )
}

export default FormlessLandingPage
